import { Info, Lightbulb, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type HintVariant = 'info' | 'tip' | 'explanation';

interface ContextualHintProps {
  children: React.ReactNode;
  variant?: HintVariant;
  icon?: LucideIcon;
  className?: string;
}

const VARIANT_STYLES: Record<HintVariant, { bg: string; border: string; icon: string; iconComponent: LucideIcon }> = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    iconComponent: Info,
  },
  tip: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    iconComponent: Lightbulb,
  },
  explanation: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'text-gray-500',
    iconComponent: Info,
  },
};

export function ContextualHint({
  children,
  variant = 'info',
  icon,
  className,
}: ContextualHintProps) {
  const styles = VARIANT_STYLES[variant];
  const IconComponent = icon || styles.iconComponent;

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        styles.bg,
        styles.border,
        className
      )}
    >
      <div className="flex items-start gap-3">
        <IconComponent className={cn('h-5 w-5 mt-0.5 shrink-0', styles.icon)} />
        <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
